import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Info, Sparkles, ArrowLeft, Undo2, Palette } from "lucide-react";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif" };

/**
 * Smart Slots — interactive demo.
 *
 * Shows how a curated Canva template with predefined "slots" (editable regions)
 * would feel from the customer's perspective:
 *
 *   [ guided form on the left ]  |  [ live-rendered preview on the right ]
 *
 * As the customer types in the form, the preview updates instantly. When they
 * hit "Save as Design", we'd normally create a real design row — here we just
 * toast a success so the demo stays self-contained (no writes to the DB).
 *
 * Slots are described declaratively (see PIZZA_TEMPLATE.slots below) — this is
 * the same shape we'd store in Mongo alongside each curated template. Building
 * the real thing = admin UI to author these slot arrays visually + a full
 * template picker + saving the filled render as a Nexso design.
 */

// ── Demo template: dark pizzeria menu ────────────────────────────────────────
// Background image is a real Pexels URL (already CDN-optimized). Slots are
// positioned as % of the 1920×1080 canvas so they scale to any preview size.
const PIZZA_TEMPLATE = {
  id: "demo-pizza",
  name: "Dark Pizzeria Menu",
  bg: "https://images.pexels.com/photos/905847/pexels-photo-905847.jpeg?auto=compress&cs=tinysrgb&w=1920",
  bgOverlay: "linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.62) 45%, rgba(0,0,0,0.85) 100%)",
  aspect: 16 / 9,
  slots: [
    {
      id: "brand",
      label: "Business name",
      type: "text",
      default: "SLICE HOUSE",
      x: 50, y: 12, w: 90, // % of canvas
      align: "center",
      fontFamily: "'Playfair Display', serif",
      fontStyle: "italic",
      fontWeight: 500,
      size: 84,
      color: "#F8FAFC",
      letterSpacing: "0.18em",
    },
    {
      id: "tagline",
      label: "Tagline",
      type: "text",
      default: "Wood-fired since 2014",
      x: 50, y: 22, w: 60,
      align: "center",
      fontFamily: "Outfit, sans-serif",
      fontWeight: 500,
      size: 22,
      color: "#FBBF24",
      letterSpacing: "0.4em",
    },
    // menu items — pairs of name + price on the same row
    { id: "item1_name",  label: "Pizza 1", type: "text",  default: "Margherita",   x: 30, y: 38, w: 40, align: "left",  fontFamily: "Outfit, sans-serif", size: 44, color: "#F8FAFC", fontWeight: 600 },
    { id: "item1_price", label: "Price 1", type: "price", default: "12.50",       x: 82, y: 38, w: 20, align: "right", fontFamily: "'Playfair Display', serif", fontStyle: "italic", size: 44, color: "#FBBF24" },

    { id: "item2_name",  label: "Pizza 2", type: "text",  default: "Pepperoni",    x: 30, y: 50, w: 40, align: "left",  fontFamily: "Outfit, sans-serif", size: 44, color: "#F8FAFC", fontWeight: 600 },
    { id: "item2_price", label: "Price 2", type: "price", default: "14.00",       x: 82, y: 50, w: 20, align: "right", fontFamily: "'Playfair Display', serif", fontStyle: "italic", size: 44, color: "#FBBF24" },

    { id: "item3_name",  label: "Pizza 3", type: "text",  default: "Quattro Formaggi", x: 30, y: 62, w: 40, align: "left",  fontFamily: "Outfit, sans-serif", size: 44, color: "#F8FAFC", fontWeight: 600 },
    { id: "item3_price", label: "Price 3", type: "price", default: "16.00",       x: 82, y: 62, w: 20, align: "right", fontFamily: "'Playfair Display', serif", fontStyle: "italic", size: 44, color: "#FBBF24" },

    { id: "phone", label: "Phone number", type: "text", default: "(555) 123-4567", x: 50, y: 88, w: 90, align: "center", fontFamily: "Outfit, sans-serif", size: 26, color: "#F8FAFC", letterSpacing: "0.25em" },
  ],
};

const defaultsFor = (tpl) => Object.fromEntries(tpl.slots.map((s) => [s.id, s.default]));

export default function SmartSlotsDemo() {
  const tpl = PIZZA_TEMPLATE;
  const [values, setValues] = useState(() => defaultsFor(tpl));
  const [accentColor, setAccentColor] = useState("#FBBF24");
  const [hoverSlot, setHoverSlot] = useState(null);

  const setVal = (id, v) => setValues((prev) => ({ ...prev, [id]: v }));
  const reset = () => setValues(defaultsFor(tpl));

  // Group the flat slot list into paired rows for a cleaner form. Anything
  // ending in "_name" pairs with the same-numbered "_price" beside it.
  const grouped = useMemo(() => {
    const rows = [];
    const seen = new Set();
    for (const s of tpl.slots) {
      if (seen.has(s.id)) continue;
      if (s.id.endsWith("_name")) {
        const pairId = s.id.replace("_name", "_price");
        const pair = tpl.slots.find((x) => x.id === pairId);
        if (pair) {
          rows.push({ kind: "pair", left: s, right: pair });
          seen.add(s.id); seen.add(pair.id);
          continue;
        }
      }
      rows.push({ kind: "single", slot: s });
      seen.add(s.id);
    }
    return rows;
  }, [tpl]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-[1400px] text-slate-900">
        <Link to="/dashboard/templates" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4" data-testid="back-to-templates">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to templates
        </Link>

        <div className="mb-6 flex flex-wrap items-baseline gap-x-4 gap-y-2">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-2">Demo · Smart Slots</div>
            <h1 className="text-3xl md:text-4xl tracking-tight font-semibold" style={headingFont}>
              Fill in the blanks. <span style={accentFont} className="italic text-sky-600">No design skills required.</span>
            </h1>
          </div>
          <div className="rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1.5" data-testid="demo-banner">
            <Info className="h-3 w-3" /> Interactive demo · nothing is saved
          </div>
        </div>

        {/* Two-pane layout: form left · preview right */}
        <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start">
          {/* Form pane */}
          <div className="rounded-2xl border bg-white p-5 sticky top-4" style={{ borderColor: "#E2E8F0" }} data-testid="smart-slots-form">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Template</div>
                <div className="text-base font-semibold tracking-tight" style={headingFont}>{tpl.name}</div>
              </div>
              <Button
                onClick={reset}
                variant="outline"
                size="sm"
                className="rounded-full h-8 text-xs border-slate-200 text-slate-600 hover:bg-slate-50"
                data-testid="reset-btn"
              >
                <Undo2 className="h-3 w-3 mr-1" /> Reset
              </Button>
            </div>

            <div className="space-y-4">
              {grouped.map((row, i) => {
                if (row.kind === "single") {
                  return (
                    <SlotInput
                      key={row.slot.id}
                      slot={row.slot}
                      value={values[row.slot.id]}
                      onChange={(v) => setVal(row.slot.id, v)}
                      onFocus={() => setHoverSlot(row.slot.id)}
                      onBlur={() => setHoverSlot(null)}
                    />
                  );
                }
                return (
                  <div key={`row-${i}`} className="grid grid-cols-[1fr_100px] gap-2">
                    <SlotInput slot={row.left} value={values[row.left.id]} onChange={(v) => setVal(row.left.id, v)} onFocus={() => setHoverSlot(row.left.id)} onBlur={() => setHoverSlot(null)} />
                    <SlotInput slot={row.right} value={values[row.right.id]} onChange={(v) => setVal(row.right.id, v)} onFocus={() => setHoverSlot(row.right.id)} onBlur={() => setHoverSlot(null)} />
                  </div>
                );
              })}

              {/* Accent color picker */}
              <div className="pt-4 border-t" style={{ borderColor: "#E2E8F0" }}>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium mb-2 flex items-center gap-1.5"><Palette className="h-3 w-3" /> Accent color</div>
                <div className="flex gap-1.5">
                  {["#FBBF24", "#F97316", "#EF4444", "#10B981", "#38BDF8", "#A855F7", "#EC4899"].map((c) => (
                    <button
                      key={c}
                      onClick={() => setAccentColor(c)}
                      aria-label={`Accent ${c}`}
                      data-testid={`accent-${c}`}
                      className={`h-7 w-7 rounded-full transition-transform ${accentColor === c ? "ring-2 ring-slate-900 ring-offset-2 scale-110" : ""}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <Button
              onClick={() => toast.success("In the real thing, this would save a Nexso design & you could push it to a TV.")}
              className="w-full mt-6 rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm font-semibold h-11"
              data-testid="save-design-btn"
            >
              <Sparkles className="h-4 w-4 mr-1.5" /> Save as design
            </Button>
          </div>

          {/* Preview pane */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium mb-2">Live preview · 1920×1080</div>
            <TemplateCanvas tpl={tpl} values={values} accentColor={accentColor} hoverSlot={hoverSlot} />
            <p className="text-xs text-slate-500 mt-3">
              Under the hood: <code className="bg-slate-100 px-1 rounded">background image + N text/image overlays</code>. Slots are pre-positioned once by an admin (or auto-generated from the Canva design layout), then customers just type. Fully WYSIWYG · pushes to TV in one click.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// ── Left-pane input ─────────────────────────────────────────────────────────
function SlotInput({ slot, value, onChange, onFocus, onBlur }) {
  const isPrice = slot.type === "price";
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-medium mb-1 block" data-testid={`label-${slot.id}`}>
        {slot.label}
      </label>
      <div className="relative">
        {isPrice && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-mono">$</span>}
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          inputMode={isPrice ? "decimal" : "text"}
          className={`rounded-xl h-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 ${isPrice ? "pl-7 font-mono" : ""}`}
          data-testid={`slot-${slot.id}`}
        />
      </div>
    </div>
  );
}

// ── Right-pane WYSIWYG canvas ───────────────────────────────────────────────
function TemplateCanvas({ tpl, values, accentColor, hoverSlot }) {
  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden border shadow-2xl"
      style={{ borderColor: "#E2E8F0", aspectRatio: `${tpl.aspect}` }}
      data-testid="canvas-preview"
    >
      {/* Background image */}
      <img src={tpl.bg} alt="" className="absolute inset-0 w-full h-full object-cover" loading="eager" decoding="async" />
      {/* Dim overlay for legibility */}
      <div className="absolute inset-0" style={{ background: tpl.bgOverlay }} />

      {/* Slots rendered as absolutely-positioned text overlays */}
      {tpl.slots.map((s) => {
        const value = values[s.id] ?? "";
        // Prices use the accent color, everything else keeps its default color
        const color = s.type === "price" || s.color === "#FBBF24" ? accentColor : s.color;
        // Font size is % of canvas height so it scales with the preview
        const fontSizeVw = (s.size / 1080) * 100;
        const displayValue = s.type === "price" ? `$${value}` : value;
        const highlighted = hoverSlot === s.id;
        return (
          <div
            key={s.id}
            data-testid={`slot-render-${s.id}`}
            className="absolute pointer-events-none"
            style={{
              left: `${s.x - (s.align === "right" ? s.w : s.align === "center" ? s.w / 2 : 0)}%`,
              top: `${s.y}%`,
              width: `${s.w}%`,
              textAlign: s.align,
              transform: "translateY(-50%)",
              transition: "outline 0.2s",
              outline: highlighted ? "2px dashed rgba(56,189,248,0.75)" : "none",
              outlineOffset: "6px",
              borderRadius: "6px",
            }}
          >
            <span
              style={{
                fontFamily: s.fontFamily,
                fontStyle: s.fontStyle || "normal",
                fontWeight: s.fontWeight || 400,
                fontSize: `${fontSizeVw}cqh`,
                color: color,
                letterSpacing: s.letterSpacing || "normal",
                display: "inline-block",
                lineHeight: 1.1,
                textShadow: "0 2px 12px rgba(0,0,0,0.55)",
              }}
            >
              {displayValue}
            </span>
          </div>
        );
      })}

      {/* Subtle "made in Nexso" watermark corner */}
      <div className="absolute bottom-3 right-3 text-white/50 text-[10px] tracking-[0.25em] uppercase">Nexso · Live preview</div>
    </div>
  );
}
